import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../components/OrganizationProvider';
import { track } from './analytics';
import { getAudience, getScopes } from './auth0Utils';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { BankInquiryResult, CreateLinkTokenResponse, GetOrderResponse, PayloadTransactionSummary, PortalNotification, PortalNotificationReference, PortalUpload } from '../types';
import { apiRoutes } from './apiRoutes';
import { useOptimisticUpdateNotificationAndOrderNotification } from './optimisticUpdates';
import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
const origin = process.env.REACT_APP_API_ORIGIN ?? ''

type ApiQueryKey = readonly [string, ...readonly string[]] 

type ApiOptionsType<TQueryFnData, TData = TQueryFnData> = Omit<UseQueryOptions<TQueryFnData, Error, TData, ApiQueryKey>, 'queryKey' | 'queryFn'>

export function useGetOrder(orderKey: string, search: string, queryOptions?: ApiOptionsType<GetOrderResponse>) {
  const response = useApiQuery<GetOrderResponse>(apiRoutes.getOrder(orderKey, search), {enabled: !!orderKey, useErrorBoundary: true, ...queryOptions});

  const [searchParams, setSearchParams] = useSearchParams();

  // Add/change role param if there is none or if the current query param doesn't match.
  useEffect(() => {
    if (!response.error && response.data?.Role) {
      const currentRoleParam = searchParams.get('role')

      if (response.data.Role !== currentRoleParam) {
        console.log('useGetOrder updating role param', response.data.Role);
        searchParams.set('role', response.data.Role);
        setSearchParams(searchParams);
      }
    }

  }, [response, searchParams, setSearchParams])


  return response;
}

export function useGetNotification(orderKey: string, notificationId: string, search: string, queryOptions?: ApiOptionsType<PortalNotification>) {
  const { optimisticUpdateNotificationAndOrderNotification } = useOptimisticUpdateNotificationAndOrderNotification()
  return useApiQuery<PortalNotification>(apiRoutes.getNotification(orderKey, notificationId, search), {
    onSuccess: () => {
      optimisticUpdateNotificationAndOrderNotification({ orderKey, notificationId, search, patch: { ReadReceipt: true } })
    },
    ...queryOptions
  });
}

export function useGetPayloadMetaData(orderKey: string, notificationId: string, search: string, queryOptions?: ApiOptionsType<PayloadTransactionSummary[]>) {
  return useApiQuery<PayloadTransactionSummary[]>(apiRoutes.getPayloadMetaData(orderKey, notificationId, search), queryOptions);
}

export function useGetNotifications(orderKey: string, search: string, queryOptions?: ApiOptionsType<PortalNotificationReference[]>) {
  return useApiQuery<PortalNotificationReference[]>(apiRoutes.getOrderNotifications(orderKey, search), queryOptions);
}

export function useGetNotificationStatusesV2(orderKey: string, notificationKey: string, search: string, queryOptions?: ApiOptionsType<Partial<PortalNotification>>) {
  return useApiQuery<Partial<PortalNotification>>(apiRoutes.getNotificationStatusesV2(orderKey, notificationKey, search), { refetchOnWindowFocus: true, ...queryOptions });
}

export function useGetUploads(companyIdOrderIdHash: string, search: string, queryOptions?: ApiOptionsType<PortalUpload[]>) {
  return useApiQuery<PortalUpload[]>(apiRoutes.getUploads(companyIdOrderIdHash, search), {
    ...queryOptions,
    refetchOnWindowFocus: true,
  })
}

export function useGetUploadsForNotification(companyIdOrderIdHash: string, notificationId: string, search: string, queryOptions?: ApiOptionsType<PortalUpload[]>) {
  return useApiQuery<PortalUpload[]>(apiRoutes.getUploads(companyIdOrderIdHash, search), {
    ...queryOptions,
    refetchOnWindowFocus: true,
    select: (portalUploads: PortalUpload[]) => {
      const portalUploadsForNotificationId = portalUploads.filter((portalUpload) => portalUpload.NotificationId === notificationId);
      return portalUploadsForNotificationId
    },
  })
}

export function useCreateLinkToken(orderKey: string, notificationId: string, search: string, queryOptions?: ApiOptionsType<CreateLinkTokenResponse>) {
  return useApiQuery<CreateLinkTokenResponse>(apiRoutes.createLinkToken(orderKey, notificationId, search), {
    ...queryOptions
  })
}

export function useFormRetrieve(orderKey: string, notificationId: string, formKey: string, search: string, queryOptions?: ApiOptionsType<any>) {
  return useApiQuery<any>(apiRoutes.formRetrieve(orderKey, notificationId, formKey, search), {
    ...queryOptions
  })
}


export function useGetBankInformation(orderKey: string, notificationId: string, routingNumber: string, queryOptions?: ApiOptionsType<BankInquiryResult>) {
  return useApiQuery<BankInquiryResult>(
    apiRoutes.bankInformation(orderKey, notificationId, routingNumber), {
      ...queryOptions
    }
  )
}

export function useGetCustomFields(orderKey: string, search: string, queryOptions?: ApiOptionsType<{ USBankRoutingVerification: boolean }>) {
  return useApiQuery<{ USBankRoutingVerification: boolean }>(
    apiRoutes.getCustomFields(orderKey, search), {
      staleTime: Infinity,
      ...queryOptions
    }
  )
}

export function useApiQuery<TResponse, TResult = TResponse>(
  path: string,
  queryOptions?: ApiOptionsType<TResponse, TResult>,
  queryKeyAddon?: string[] 
) {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const organization = useOrganization();
  const scope = getScopes(organization?.MfaRequired);
  const audience = getAudience(organization?.MfaRequired);

  const queryKey: ApiQueryKey = queryKeyAddon && Array.isArray(queryKeyAddon) ? [path, ...queryKeyAddon] : [path]

  const STALE_TIME_20S = 1000 * 20

  return useQuery({
    staleTime: STALE_TIME_20S,
    refetchOnWindowFocus: false,
    ...queryOptions,
    queryKey,
    queryFn: async () => {
      const url = `${origin}/api/${path}`;

      // Try getting the token silently
      let accessToken = '';
      try {
        accessToken = await getAccessTokenSilently({ audience, scope });
      } catch (e) {
        console.warn('getAccessTokenSilently() FAILED, redirecting to login…', e);
        await loginWithRedirect({
          appState: { returnTo: window.location.pathname + window.location.search }
        });
        throw new Error('Authentication required');
      }


      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (res.ok) {
        try {
          const jsonResponse = await res.json();
          console.log('useApiTanstack', { path, jsonResponse });
          return jsonResponse as TResponse;
        } catch (e) {
          // Handle empty response
          track('error', 'useApiTanstack', {}, {
            message: 'Empty success response, returning empty object',
            error: JSON.stringify(e),
          });
          return {} as TResponse;
        }
      } else {
        let errorData: any = null;
        try {
          errorData = await res.json();
        } catch (e) {
          track('error', 'useApiTanstack', {}, {
            message: 'Failed to parse error response as JSON',
            error: JSON.stringify(e),
          });
          errorData = { userErrorMessage: 'An error occurred' };
        }
        throw errorData;
      }
    }
  });
}
